import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { router } from './routes';
import '@common/musicHubCommon/tokens.css';

// antd 主题配置 - 基础配置，详细样式由组件库覆盖
const antdTheme = {
  token: {
    colorPrimary: '#ffffff',
    colorBgContainer: 'rgba(255, 255, 255, 0.06)',
    colorText: '#ffffff',
    colorTextSecondary: 'rgba(255, 255, 255, 0.7)',
    colorBorder: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 16,
    fontFamily: 'Poppins, sans-serif',
  },
  components: {
    Button: {
      primaryColor: '#121212',
      defaultBg: 'transparent',
      defaultBorderColor: 'rgba(255, 255, 255, 0.15)',
    },
    Input: {
      activeBg: 'rgba(255, 255, 255, 0.06)',
      hoverBg: 'rgba(255, 255, 255, 0.06)',
    },
    Select: {
      optionSelectedBg: 'rgba(147, 97, 255, 0.2)',
    },
  },
};

// 全局样式
const globalStyles = document.createElement('style');
globalStyles.textContent = `
  * {
    box-sizing: border-box;
  }
  html, body {
    margin: 0;
    padding: 0;
    min-height: 100vh;
    background-color: #121212;
    color: #ffffff;
    font-family: 'Poppins', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  #root {
    min-height: 100vh;
  }
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  a {
    color: #9361ff;
    text-decoration: none;
  }
  .page-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background-color: #121212;
  }
  .page-loading__spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: #9361ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(globalStyles);

// 表单必填标记覆盖样式（放在最后确保优先级最高）
const formRequiredStyles = document.createElement('style');
formRequiredStyles.textContent = `
  /* 表单必填标记：红色星号在右边 - 最高优先级覆盖 */
  .ant-form-item-label > label.ant-form-item-required::before,
  .ant-form .ant-form-item-label > label.ant-form-item-required::before,
  .ant-form-vertical .ant-form-item-label > label.ant-form-item-required::before,
  .ant-form-horizontal .ant-form-item-label > label.ant-form-item-required::before {
    display: none !important;
    content: '' !important;
  }
  .ant-form-item {
    margin-bottom: 0 !important;
  }
  .ant-form-item-label > label.ant-form-item-required::after,
  .ant-form .ant-form-item-label > label.ant-form-item-required::after,
  .ant-form-vertical .ant-form-item-label > label.ant-form-item-required::after,
  .ant-form-horizontal .ant-form-item-label > label.ant-form-item-required::after {
    display: inline-block !important;
    margin-inline-start: 4px !important;
    margin-inline-end: 0 !important;
    color: #e53935 !important;
    font-size: 14px !important;
    font-family: SimSun, sans-serif !important;
    line-height: 1 !important;
    content: '*' !important;
    visibility: visible !important;
  }

  /* DatePicker 弹窗暗色主题 - 最高优先级覆盖 */
  .ant-picker-dropdown {
    z-index: 10000 !important;
  }
  .ant-picker-dropdown .ant-picker-panel-container {
    background: #2a2a2a !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
  }
  .ant-picker-dropdown .ant-picker-panel {
    background: #2a2a2a !important;
    border: none !important;
  }
  .ant-picker-dropdown .ant-picker-header {
    color: #fff !important;
    border-bottom-color: rgba(255, 255, 255, 0.1) !important;
  }
  .ant-picker-dropdown .ant-picker-header button {
    color: rgba(255, 255, 255, 0.7) !important;
  }
  .ant-picker-dropdown .ant-picker-header button:hover {
    color: #fff !important;
  }
  .ant-picker-dropdown .ant-picker-header-view button {
    color: #fff !important;
    font-weight: 600 !important;
  }
  .ant-picker-dropdown .ant-picker-content th {
    color: rgba(255, 255, 255, 0.5) !important;
  }
  .ant-picker-dropdown .ant-picker-cell {
    color: rgba(255, 255, 255, 0.35) !important;
  }
  .ant-picker-dropdown .ant-picker-cell-in-view {
    color: #fff !important;
  }
  .ant-picker-dropdown .ant-picker-cell:hover:not(.ant-picker-cell-selected):not(.ant-picker-cell-disabled) .ant-picker-cell-inner {
    background: rgba(147, 97, 255, 0.2) !important;
  }
  .ant-picker-dropdown .ant-picker-cell-selected .ant-picker-cell-inner,
  .ant-picker-dropdown .ant-picker-cell-selected:hover .ant-picker-cell-inner {
    background: #9361ff !important;
    color: #fff !important;
  }
  .ant-picker-dropdown .ant-picker-cell-today .ant-picker-cell-inner::before {
    border-color: #9361ff !important;
  }
  .ant-picker-dropdown .ant-picker-cell-disabled {
    color: rgba(255, 255, 255, 0.2) !important;
  }
  .ant-picker-dropdown .ant-picker-footer {
    border-top-color: rgba(255, 255, 255, 0.1) !important;
    background: #2a2a2a !important;
  }
  .ant-picker-dropdown .ant-picker-today-btn,
  .ant-picker-dropdown .ant-picker-now-btn {
    color: #9361ff !important;
  }
  .ant-picker-dropdown .ant-picker-ok button {
    background: #9361ff !important;
    border-color: #9361ff !important;
  }
  /* 时间选择器面板 */
  .ant-picker-dropdown .ant-picker-time-panel {
    border-left-color: rgba(255, 255, 255, 0.1) !important;
  }
  .ant-picker-dropdown .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner {
    color: rgba(255, 255, 255, 0.7) !important;
  }
  .ant-picker-dropdown .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner:hover {
    background: rgba(147, 97, 255, 0.2) !important;
  }
  .ant-picker-dropdown .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
    background: #9361ff !important;
    color: #fff !important;
  }
  .ant-picker-dropdown .ant-picker-datetime-panel .ant-picker-date-panel {
    border-right-color: rgba(255, 255, 255, 0.1) !important;
  }
`;
document.body.appendChild(formRequiredStyles);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ConfigProvider theme={antdTheme}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </ConfigProvider>
  </React.StrictMode>,
);
